import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FolderKanban, Loader2, ArrowLeft, Pencil, Trash2, Plus, Ban, AlertTriangle,
  CheckCircle2, Circle, CalendarClock, MapPin, ListChecks, GitBranch, Paperclip,
  StickyNote, History as HistoryIcon, X, Download, Link2, Unlink, Flag, Milestone,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@/components/StatusPill";
import { DEPT_COLOUR } from "@/lib/departments";
import ProjectForm from "@/components/ProjectForm";
import { StatusChip } from "@/pages/ProjectsPage";
import { ProjectRevenue } from "@/components/ProjectRevenue";

const TABS = [
  { key: "overview", label: "Overview", icon: ListChecks },
  { key: "tasks", label: "Tasks", icon: FolderKanban },
  { key: "plan", label: "Phases & Milestones", icon: GitBranch },
  { key: "files", label: "Files", icon: Paperclip },
  { key: "revenue", label: "Revenue", icon: Paperclip },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "history", label: "History", icon: HistoryIcon },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => setData(r.data)).catch(() => setData(false)), [id]);
  useEffect(() => { load(); }, [load]);

  const del = async () => {
    if (!window.confirm("Delete this project? Linked jobs are kept and simply unlinked.")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted"); navigate("/projects"); }
    catch { toast.error("Could not delete project"); }
  };

  if (data === null) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading project…</div>;
  if (data === false) return <div className="p-10 text-slate-500">Project not found.</div>;

  const { project, overview, tasks, can_manage } = data;

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="page-project-detail">
      <button onClick={() => navigate("/projects")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-4" data-testid="project-back">
        <ArrowLeft className="w-4 h-4" /> All projects
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 lg:p-6 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><FolderKanban className="w-6 h-6" /></span>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{project.name}</h1>
                <StatusChip status={project.status} />
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {project.customer_name || "No customer"}
                {project.site_address?.postcode && <> · {[project.site_address.city, project.site_address.postcode].filter(Boolean).join(", ")}</>}
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                {project.owner_name && <span>Owner: <span className="text-slate-600 font-medium">{project.owner_name}</span></span>}
                {project.target_start && <span>Start: {project.target_start}</span>}
                {project.target_end && <span>Target: {project.target_end}</span>}
              </div>
            </div>
          </div>
          {can_manage && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setEditing(true)} className="gap-1.5" data-testid="project-edit-btn"><Pencil className="w-4 h-4" /> Edit</Button>
              <Button variant="outline" onClick={del} className="gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid="project-delete-btn"><Trash2 className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
        {project.description && <p className="text-sm text-slate-600 mt-4 leading-relaxed border-t border-slate-100 pt-4">{project.description}</p>}
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`project-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab overview={overview} project={project} />}
      {tab === "tasks" && <TasksTab pid={id} project={project} tasks={tasks} canManage={can_manage} reload={load} canOrders={can("orders.manage")} />}
      {tab === "plan" && <PlanTab pid={id} project={project} canManage={can_manage} reload={load} />}
      {tab === "files" && <FilesTab pid={id} files={project.files || []} canManage={can_manage} reload={load} />}
      {tab === "notes" && <NotesTab pid={id} notes={project.notes || []} canManage={can_manage} reload={load} />}
      {tab === "history" && <HistoryTab history={project.history || []} />}
      {tab === "revenue" && <ProjectRevenue pid={id} canManage={can_manage || can("finance.manage")} />}

      {editing && <ProjectForm mode="edit" project={project} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}

// --------------------------------------------------------------- Overview
function Metric({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function OverviewTab({ overview, project }) {
  const o = overview;
  return (
    <div className="space-y-5" data-testid="project-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="Progress" value={`${o.progress_pct}%`} tone="text-indigo-600" />
        <Metric label="Tasks" value={`${o.complete_tasks}/${o.total_tasks}`} />
        <Metric label="Blocked" value={o.blocked.length} tone={o.blocked.length ? "text-rose-600" : "text-slate-900"} />
        <Metric label="Overdue" value={o.overdue_tasks.length} tone={o.overdue_tasks.length ? "text-amber-600" : "text-slate-900"} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="project-next-action">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Next action</div>
        <div className="text-sm text-slate-800 font-medium mt-1.5">{o.next_action || "No upcoming action — all caught up."}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Upcoming visits" icon={CalendarClock}>
          {o.upcoming_visits.length === 0 ? <Empty text="No upcoming visits scheduled." /> : (
            <ul className="space-y-2">
              {o.upcoming_visits.map((v, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="text-xs font-semibold tabular-nums text-indigo-600 w-24 shrink-0">{v.date}</span>
                  <span className="text-slate-700 truncate">{v.title || v.task_legacy_id}</span>
                  {v.location && <span className="text-slate-400 text-xs ml-auto inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{v.location}</span>}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Blockers" icon={Ban}>
          {o.blocked.length === 0 ? <Empty text="No blockers." /> : (
            <ul className="space-y-2">
              {o.blocked.map((b) => (
                <li key={b.legacy_id} className="text-sm">
                  <Link to={`/tasks/${b.legacy_id}`} className="font-medium text-rose-700 hover:underline">{b.title}</Link>
                  {b.reason && <p className="text-xs text-slate-500 mt-0.5">{b.reason}</p>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {o.overdue_tasks.length > 0 && (
        <Panel title="Overdue tasks" icon={AlertTriangle}>
          <ul className="space-y-1.5">
            {o.overdue_tasks.map((t) => (
              <li key={t.legacy_id}><Link to={`/tasks/${t.legacy_id}`} className="text-sm text-amber-700 hover:underline font-medium">{t.title}</Link></li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Milestones" icon={Milestone}>
        <ul className="space-y-2">
          {(project.milestones || []).map((m) => (
            <li key={m.id} className="flex items-center gap-2.5 text-sm">
              {m.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
              <span className={m.done ? "text-slate-400 line-through" : "text-slate-700"}>{m.label}</span>
              {m.target_date && <span className="text-xs text-slate-400 ml-auto tabular-nums">{m.done ? `done ${m.done_date || ""}` : m.target_date}</span>}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mb-3">{Icon && <Icon className="w-4 h-4 text-indigo-500" />}{title}</h3>
      {children}
    </div>
  );
}
function Empty({ text }) { return <p className="text-sm text-slate-400">{text}</p>; }

// --------------------------------------------------------------- Tasks
function TasksTab({ pid, project, tasks, canManage, reload, canOrders }) {
  const [view, setView] = useState("list");
  const [showAdd, setShowAdd] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [blocking, setBlocking] = useState(null);

  const phases = [...(project.phases || [])].sort((a, b) => a.order - b.order);
  const groups = [...phases.map((p) => ({ ...p })), { id: null, name: "Unassigned" }]
    .map((p) => ({ ...p, tasks: tasks.filter((t) => (t.phase_id || null) === (p.id || null)) }))
    .filter((g) => g.tasks.length > 0 || g.id);

  const setPhase = async (legacy_id, phase_id) => {
    try { await api.patch(`/projects/${pid}/tasks/${legacy_id}`, { phase_id: phase_id || null }); reload(); }
    catch { toast.error("Could not move task"); }
  };
  const unlink = async (legacy_id) => {
    if (!window.confirm("Unlink this job from the project? The job itself is kept.")) return;
    try { await api.delete(`/projects/${pid}/tasks/${legacy_id}`); toast.success("Job unlinked"); reload(); }
    catch { toast.error("Could not unlink job"); }
  };

  return (
    <div data-testid="project-tasks">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {["list", "timeline"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`project-view-${v}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${view === v ? "bg-white shadow-sm text-indigo-700" : "text-slate-500"}`}>{v}</button>
          ))}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowLink(true)} className="gap-1.5" data-testid="project-link-task-btn"><Link2 className="w-4 h-4" /> Link existing</Button>
            {canOrders && <Button onClick={() => setShowAdd(true)} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="project-add-task-btn"><Plus className="w-4 h-4" /> New task</Button>}
          </div>
        )}
      </div>

      {tasks.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="project-tasks-empty">No tasks in this project yet.</div>
      ) : view === "timeline" ? (
        <Timeline tasks={[...tasks].sort((a, b) => (a.est_install_from || "").localeCompare(b.est_install_from || ""))} />
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.id || "unassigned"} data-testid={`project-phase-group-${g.id || "unassigned"}`}>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{g.name} <span className="text-slate-300">· {g.tasks.length}</span></h3>
              <div className="space-y-2">
                {g.tasks.map((t) => (
                  <TaskRow key={t.legacy_id} t={t} phases={phases} canManage={canManage}
                    onPhase={(pidv) => setPhase(t.legacy_id, pidv)} onUnlink={() => unlink(t.legacy_id)} onBlock={() => setBlocking(t)} />
                ))}
                {g.tasks.length === 0 && <p className="text-sm text-slate-400 pl-1">No tasks in this phase.</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddTaskModal pid={pid} phases={phases} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}
      {showLink && <LinkTaskModal pid={pid} phases={phases} onClose={() => setShowLink(false)} onSaved={() => { setShowLink(false); reload(); }} />}
      {blocking && <BlockModal pid={pid} task={blocking} onClose={() => setBlocking(null)} onSaved={() => { setBlocking(null); reload(); }} />}
    </div>
  );
}

function TaskRow({ t, phases, canManage, onPhase, onUnlink, onBlock }) {
  return (
    <div className={`bg-white border rounded-xl p-3.5 flex items-center gap-3 ${t.blocked ? "border-rose-200" : "border-slate-200"}`} data-testid={`project-task-${t.legacy_id}`}>
      {t.complete ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : t.blocked ? <Ban className="w-5 h-5 text-rose-500 shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/tasks/${t.legacy_id}`} className="text-sm font-semibold text-slate-900 hover:text-indigo-600 hover:underline truncate">{t.title}</Link>
          {t.current_department && <Pill colour={DEPT_COLOUR[t.current_department]}>{t.current_department}</Pill>}
          {t.blocked && <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">Blocked</span>}
        </div>
        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono">{t.legacy_id}</span>
          {t.work_type && <span>· {t.work_type}</span>}
          {t.est_install_from && <span>· {t.est_install_from}</span>}
          {t.blocked && t.blocked_reason && <span className="text-rose-500">· {t.blocked_reason}</span>}
        </div>
      </div>
      {canManage && (
        <div className="flex items-center gap-1.5 shrink-0">
          <select value={t.phase_id || ""} onChange={(e) => onPhase(e.target.value)} data-testid={`project-task-phase-${t.legacy_id}`}
            className="text-xs rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-600 max-w-[130px]">
            <option value="">No phase</option>
            {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={onBlock} title={t.blocked ? "Clear blocker" : "Mark blocked"} data-testid={`project-task-block-${t.legacy_id}`}
            className={`p-1.5 rounded-lg ${t.blocked ? "text-rose-600 bg-rose-50" : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"}`}><Ban className="w-4 h-4" /></button>
          <button onClick={onUnlink} title="Unlink from project" data-testid={`project-task-unlink-${t.legacy_id}`}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><Unlink className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

function Timeline({ tasks }) {
  return (
    <div className="relative pl-6" data-testid="project-timeline">
      <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" />
      <div className="space-y-4">
        {tasks.map((t) => (
          <div key={t.legacy_id} className="relative">
            <span className={`absolute -left-[18px] top-1.5 w-3 h-3 rounded-full border-2 border-white ${t.complete ? "bg-emerald-500" : t.blocked ? "bg-rose-500" : "bg-indigo-400"}`} />
            <div className="bg-white border border-slate-200 rounded-xl p-3.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold tabular-nums text-indigo-600">{t.est_install_from || "TBC"}</span>
                <Link to={`/tasks/${t.legacy_id}`} className="text-sm font-semibold text-slate-900 hover:text-indigo-600 hover:underline">{t.title}</Link>
                {t.phase_name && <span className="text-[11px] text-slate-400">· {t.phase_name}</span>}
                {t.blocked && <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">Blocked</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddTaskModal({ pid, phases, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", phase_id: "", work_type: "", est_install_from: "", est_install_to: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try { await api.post(`/projects/${pid}/tasks`, { ...f, phase_id: f.phase_id || null }); toast.success("Task created"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create task"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New task in project" onClose={onClose} testId="project-add-task-modal">
      <div className="space-y-3">
        <Input placeholder="Task title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="add-task-title" />
        <select value={f.phase_id} onChange={(e) => set("phase_id", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm" data-testid="add-task-phase">
          <option value="">No phase</option>
          {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Input placeholder="Work type (optional)" value={f.work_type} onChange={(e) => set("work_type", e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-slate-500">Est. from</label><input type="date" value={f.est_install_from} onChange={(e) => set("est_install_from", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">Est. to</label><input type="date" value={f.est_install_to} onChange={(e) => set("est_install_to", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" /></div>
        </div>
      </div>
      <ModalFooter onClose={onClose} onSave={save} busy={busy} saveLabel="Create task" testId="add-task-save" />
    </Modal>
  );
}

function LinkTaskModal({ pid, phases, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState({});
  const [phaseId, setPhaseId] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const r = await api.get("/tasks", { params: { q, page_size: 15 } }); setResults(r.data.items || []); } catch {}
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const ids = Object.keys(picked).filter((k) => picked[k]);
  const save = async () => {
    if (ids.length === 0) { toast.error("Select at least one job"); return; }
    setBusy(true);
    try { await api.post(`/projects/${pid}/tasks/link`, { legacy_ids: ids, phase_id: phaseId || null }); toast.success(`Linked ${ids.length} job(s)`); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not link jobs"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Link existing jobs" onClose={onClose} testId="project-link-task-modal">
      <Input placeholder="Search jobs by title, ID, postcode…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="link-task-search" autoFocus />
      <div className="mt-3 max-h-64 overflow-auto space-y-1.5">
        {results.length === 0 && q.length >= 2 && <p className="text-sm text-slate-400 py-4 text-center">No matching jobs you can see.</p>}
        {results.map((t) => (
          <label key={t.legacy_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`link-task-option-${t.legacy_id}`}>
            <input type="checkbox" checked={!!picked[t.legacy_id]} onChange={() => toggle(t.legacy_id)} className="accent-indigo-600" />
            <span className="min-w-0 flex-1">
              <span className="text-sm font-medium text-slate-800 truncate block">{t.title}</span>
              <span className="text-xs text-slate-400 font-mono">{t.legacy_id}{t.current_department ? ` · ${t.current_department}` : ""}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="mt-3">
        <label className="text-xs text-slate-500">Assign to phase (optional)</label>
        <select value={phaseId} onChange={(e) => setPhaseId(e.target.value)} className="w-full mt-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm" data-testid="link-task-phase">
          <option value="">No phase</option>
          {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <ModalFooter onClose={onClose} onSave={save} busy={busy} saveLabel={`Link ${ids.length || ""} job(s)`} testId="link-task-save" />
    </Modal>
  );
}

function BlockModal({ pid, task, onClose, onSaved }) {
  const [reason, setReason] = useState(task.blocked_reason || "");
  const [busy, setBusy] = useState(false);
  const isBlocked = task.blocked;
  const save = async (blocked) => {
    setBusy(true);
    try { await api.post(`/projects/${pid}/tasks/${task.legacy_id}/block`, { blocked, reason }); toast.success(blocked ? "Task blocked" : "Blocker cleared"); onSaved(); }
    catch { toast.error("Could not update blocker"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={isBlocked ? "Blocked task" : "Mark task blocked"} onClose={onClose} testId="project-block-modal">
      <p className="text-sm text-slate-600 mb-3">{task.title}</p>
      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reason</label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" placeholder="Why is this task blocked?" data-testid="block-reason-input" />
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        {isBlocked && <Button variant="outline" onClick={() => save(false)} disabled={busy} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid="block-clear">Clear blocker</Button>}
        <Button onClick={() => save(true)} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5" data-testid="block-confirm">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Mark blocked</Button>
      </div>
    </Modal>
  );
}

// --------------------------------------------------------- Phases & Milestones
function PlanTab({ pid, project, canManage, reload }) {
  const [phaseName, setPhaseName] = useState("");
  const [ms, setMs] = useState({ label: "", target_date: "" });
  const phases = [...(project.phases || [])].sort((a, b) => a.order - b.order);

  const addPhase = async () => {
    if (!phaseName.trim()) return;
    try { await api.post(`/projects/${pid}/phases`, { name: phaseName.trim() }); setPhaseName(""); reload(); } catch { toast.error("Could not add phase"); }
  };
  const delPhase = async (phId) => { if (!window.confirm("Remove this phase? Tasks stay but lose their phase.")) return; try { await api.delete(`/projects/${pid}/phases/${phId}`); reload(); } catch { toast.error("Could not remove phase"); } };
  const addMs = async () => { if (!ms.label.trim()) return; try { await api.post(`/projects/${pid}/milestones`, { label: ms.label.trim(), target_date: ms.target_date || null }); setMs({ label: "", target_date: "" }); reload(); } catch { toast.error("Could not add milestone"); } };
  const toggleMs = async (m) => { try { await api.patch(`/projects/${pid}/milestones/${m.id}`, { done: !m.done }); reload(); } catch { toast.error("Could not update milestone"); } };
  const delMs = async (mId) => { try { await api.delete(`/projects/${pid}/milestones/${mId}`); reload(); } catch { toast.error("Could not delete milestone"); } };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="project-plan">
      <Panel title="Phases" icon={GitBranch}>
        <div className="space-y-2 mb-3">
          {phases.length === 0 && <Empty text="No phases yet." />}
          {phases.map((p, i) => (
            <div key={p.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`plan-phase-${p.id}`}>
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="text-slate-700 flex-1">{p.name}</span>
              {canManage && <button onClick={() => delPhase(p.id)} className="text-slate-400 hover:text-rose-600" data-testid={`plan-phase-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Input placeholder="Add phase…" value={phaseName} onChange={(e) => setPhaseName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPhase()} data-testid="plan-phase-input" />
            <Button onClick={addPhase} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="plan-phase-add"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
      </Panel>

      <Panel title="Milestones" icon={Milestone}>
        <div className="space-y-2 mb-3">
          {(project.milestones || []).map((m) => (
            <div key={m.id} className="flex items-center gap-2.5 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`plan-ms-${m.id}`}>
              <button onClick={() => canManage && toggleMs(m)} disabled={!canManage} data-testid={`plan-ms-toggle-${m.id}`}>
                {m.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
              </button>
              <span className={`flex-1 ${m.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{m.label}{m.preset && <span className="ml-1.5 text-[10px] text-indigo-400 uppercase">preset</span>}</span>
              {m.target_date && <span className="text-xs text-slate-400 tabular-nums">{m.target_date}</span>}
              {canManage && !m.preset && <button onClick={() => delMs(m.id)} className="text-slate-400 hover:text-rose-600" data-testid={`plan-ms-del-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Input placeholder="Milestone…" value={ms.label} onChange={(e) => setMs((s) => ({ ...s, label: e.target.value }))} data-testid="plan-ms-input" />
            <input type="date" value={ms.target_date} onChange={(e) => setMs((s) => ({ ...s, target_date: e.target.value }))} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-sm shrink-0" data-testid="plan-ms-date" />
            <Button onClick={addMs} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="plan-ms-add"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
      </Panel>
    </div>
  );
}

// --------------------------------------------------------------- Files
function FilesTab({ pid, files, canManage, reload }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/projects/${pid}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); reload(); }
    catch (er) { toast.error(formatApiErrorDetail(er?.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const download = async (f) => {
    try { const r = await api.get(`/files/${f.path}`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = f.filename || "file"; a.click(); URL.revokeObjectURL(url); }
    catch { toast.error("Could not download"); }
  };
  const del = async (fid) => { if (!window.confirm("Delete this file?")) return; try { await api.delete(`/projects/${pid}/files/${fid}`); reload(); } catch { toast.error("Could not delete"); } };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="project-files">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Paperclip className="w-4 h-4 text-indigo-500" /> Shared project files</h3>
        {canManage && (
          <>
            <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="project-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="project-file-upload">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Upload
            </Button>
          </>
        )}
      </div>
      {files.length === 0 ? <Empty text="No shared files yet. Quotes, site plans, photos and documents kept here are visible to the whole project team." /> : (
        <div className="space-y-1.5" data-testid="project-files-list">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50" data-testid={`project-file-${f.id}`}>
              <Paperclip className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{f.filename}</div>
                <div className="text-[11px] text-slate-400">{[f.uploaded_by, f.uploaded_at?.slice(0, 10)].filter(Boolean).join(" · ")}</div>
              </div>
              <button onClick={() => download(f)} className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" data-testid={`project-file-download-${f.id}`}><Download className="w-4 h-4" /></button>
              {canManage && <button onClick={() => del(f.id)} className="p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`project-file-del-${f.id}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- Notes
function NotesTab({ pid, notes, canManage, reload }) {
  const [text, setText] = useState("");
  const add = async () => { if (!text.trim()) return; try { await api.post(`/projects/${pid}/notes`, { text: text.trim() }); setText(""); reload(); } catch { toast.error("Could not add note"); } };
  const del = async (nid) => { try { await api.delete(`/projects/${pid}/notes/${nid}`); reload(); } catch { toast.error("Could not delete note"); } };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="project-notes">
      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mb-4"><StickyNote className="w-4 h-4 text-indigo-500" /> Project notes</h3>
      {canManage && (
        <div className="flex items-start gap-2 mb-4">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a shared note…" className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="project-note-input" />
          <Button onClick={add} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="project-note-add"><Plus className="w-4 h-4" /></Button>
        </div>
      )}
      {notes.length === 0 ? <Empty text="No notes yet." /> : (
        <div className="space-y-2">
          {[...notes].reverse().map((n) => (
            <div key={n.id} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5" data-testid={`project-note-${n.id}`}>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.text}</p>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[11px] text-slate-400">{n.author} · {n.ts?.slice(0, 16).replace("T", " ")}</span>
                {canManage && <button onClick={() => del(n.id)} className="text-slate-300 hover:text-rose-600" data-testid={`project-note-del-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- History
function HistoryTab({ history }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="project-history">
      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mb-4"><HistoryIcon className="w-4 h-4 text-indigo-500" /> Change history</h3>
      {(!history || history.length === 0) ? <Empty text="No history recorded." /> : (
        <ol className="relative border-l border-slate-200 pl-5 space-y-4">
          {[...history].reverse().map((h, i) => (
            <li key={i} className="relative" data-testid={`project-history-${i}`}>
              <span className="absolute -left-[23px] top-1 w-2.5 h-2.5 rounded-full bg-indigo-400 border-2 border-white" />
              <div className="text-sm text-slate-700">{h.detail}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{h.actor} · {h.ts?.slice(0, 16).replace("T", " ")}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// --------------------------------------------------------------- Modal shell
function Modal({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testId}>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function ModalFooter({ onClose, onSave, busy, saveLabel, testId }) {
  return (
    <div className="flex items-center justify-end gap-2 mt-5">
      <Button variant="outline" onClick={onClose}>Cancel</Button>
      <Button onClick={onSave} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid={testId}>
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {saveLabel}
      </Button>
    </div>
  );
}
